import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { HeaderAccent } from "@/lib/status";
import { toast } from "sonner";
import { GraduationCap, Plus, Pencil, Trash2, BookOpen, RefreshCw, Award } from "lucide-react";
import { CourseViewer, printCertificate } from "@/components/training/CourseViewer";
import { CourseEditor } from "@/components/training/CourseEditor";
import { TrainingMatrix } from "@/components/training/TrainingMatrix";

export const TONE = { valid: "bg-sage-light text-sage-dark", expiring: "bg-gold-light text-gold-dark", expired: "bg-coral-light text-coral-dark", missing: "bg-slate-100 text-slate-500" };
export const LABEL = { valid: "Completed", expiring: "Refresher due soon", expired: "Refresher overdue", missing: "Not started" };
const AUD = { carer: "Befrienders", office: "Office staff", all: "Everyone" };

function CourseCard({ c, isAdmin, onOpen, onEdit, onDelete }) {
  return (
    <div data-testid={`course-card-${c.id}`} className="flex flex-col rounded-xl border border-border bg-white p-5 shadow-sm transition-shadow hover:shadow-md">
      <div className="flex items-start justify-between gap-2">
        <h3 className="font-heading text-lg font-semibold text-ink">{c.title}</h3>
        {c.applies && <span data-testid={`course-status-${c.id}`} className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${TONE[c.my_status]}`}>{LABEL[c.my_status]}</span>}
      </div>
      <p className="mt-1 flex-1 text-sm text-ink-soft">{c.summary}</p>
      <div className="mt-3 flex flex-wrap gap-1.5 text-[11px] font-semibold">
        <span className="rounded-full bg-brand-light px-2 py-0.5 text-brand">{AUD[c.audience]}</span>
        {c.required && <span className="rounded-full bg-coral-light px-2 py-0.5 text-coral-dark">Required</span>}
        {c.refresher_months > 0 && <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-ink-soft"><RefreshCw size={10} /> Every {c.refresher_months} months</span>}
        <span className="rounded-full bg-secondary px-2 py-0.5 text-ink-soft">{c.question_count} questions · pass {c.pass_mark}%</span>
      </div>
      {c.expires_on && <p className="mt-2 text-xs text-ink-muted">Valid until {c.expires_on}</p>}
      {["valid", "expiring"].includes(c.my_status) && c.applies && (
        <button onClick={() => printCertificate(c.id)} data-testid={`download-certificate-${c.id}`} className="mt-3 inline-flex items-center justify-center gap-1.5 rounded-lg border border-gold/50 bg-gold-light/50 px-3 py-1.5 text-sm font-semibold text-gold-dark hover:bg-gold-light">
          <Award size={15} /> Download certificate
        </button>
      )}
      <div className="mt-4 flex gap-2">
        <button onClick={() => onOpen(c)} data-testid={`open-course-${c.id}`} className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-dark">
          <BookOpen size={15} /> {c.my_status === "valid" ? "Review course" : c.my_status === "missing" ? "Start course" : "Retake course"}
        </button>
        {isAdmin && <button onClick={() => onEdit(c)} data-testid={`edit-course-${c.id}`} title="Edit" className="rounded-lg border border-border px-3 text-ink hover:bg-secondary"><Pencil size={15} /></button>}
        {isAdmin && <button onClick={() => onDelete(c)} data-testid={`delete-course-${c.id}`} title="Delete" className="rounded-lg border border-border px-3 text-coral-dark hover:bg-coral-light"><Trash2 size={15} /></button>}
      </div>
    </div>
  );
}

export default function Training() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const oversight = ["admin", "office", "viewer"].includes(user?.role);
  const [tab, setTab] = useState("courses");
  const [courses, setCourses] = useState([]);
  const [open, setOpen] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/courses").then((r) => setCourses(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const del = async (c) => {
    if (!window.confirm(`Delete "${c.title}"? It will move to the recycle bin.`)) return;
    try { await api.delete(`/courses/${c.id}`); toast.success("Course deleted"); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const mine = courses.filter((c) => c.applies);
  const done = mine.filter((c) => ["valid", "expiring"].includes(c.my_status)).length;

  return (
    <div className="space-y-6" data-testid="training-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-3xl font-bold tracking-tight text-ink"><GraduationCap size={28} className="text-brand" /> {oversight ? "Training" : "My training"}</h1>
          <p className="mt-1 text-ink-soft">Read each course, pass the short quiz and download your certificate. Required courses count towards compliance.</p>
          <HeaderAccent />
        </div>
        {isAdmin && <button onClick={() => setEdit({})} data-testid="new-course-button" className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2.5 font-semibold text-white hover:bg-brand-dark"><Plus size={18} /> New course</button>}
      </div>

      {mine.length > 0 && (
        <div className="rounded-xl border border-border bg-white p-4 shadow-sm" data-testid="training-progress">
          <div className="mb-2 flex justify-between text-sm"><span className="font-semibold text-ink">Your progress</span><span className="text-ink-soft">{done} of {mine.length} complete</span></div>
          <div className="h-2 overflow-hidden rounded-full bg-secondary"><div className="h-full rounded-full bg-sage transition-[width] duration-500" style={{ width: `${(100 * done) / mine.length}%` }} /></div>
        </div>
      )}

      {oversight && (
        <div className="flex gap-2">
          {[["courses", "Course library"], ["team", "Team progress"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`training-tab-${k}`} className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${tab === k ? "bg-brand text-white" : "bg-secondary text-ink hover:bg-brand-light"}`}>{l}</button>
          ))}
        </div>
      )}

      {tab === "team" ? <TrainingMatrix /> : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {courses.length === 0 && <p className="text-ink-muted">No courses yet.</p>}
          {courses.map((c) => <CourseCard key={c.id} c={c} isAdmin={isAdmin} onOpen={setOpen} onEdit={setEdit} onDelete={del} />)}
        </div>
      )}

      {open && <CourseViewer courseId={open.id} canTake={open.applies} onClose={() => { setOpen(null); load(); }} />}
      {edit && <CourseEditor courseId={edit.id} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}
